import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import ProtectedRoute from 'src/components/ProtectedRoute';

import SuspenseLoader from 'src/components/SuspenseLoader';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Tasks = Loader(lazy(() => import('src/content/dashboards/Tasks')));
const MainDashboard = Loader(lazy(() => import('src/content/dashboards/Main')));
const EmailsDashboard = Loader(lazy(() => import('src/content/dashboards/Emails')));
const CalendarDashboard = Loader(lazy(() => import('src/content/dashboards/Calendar')));
const TeamDashboard = Loader(lazy(() => import('src/content/dashboards/Team')));
const AgentDashboard = Loader(lazy(() => import('src/content/dashboards/Agent')));
const AgentAnalyticsDashboard = Loader(lazy(() => import('src/content/dashboards/AgentAnalytics')));
const ElevenLabsDashboard = Loader(lazy(() => import('src/content/dashboards/ElevenLabs')));
const RenderDashboard = Loader(lazy(() => import('src/content/dashboards/Render')));
const AudomeDashboard = Loader(lazy(() => import('src/content/dashboards/Audome')));
const AttendanceDashboard = Loader(lazy(() => import('src/content/dashboards/Attendance')));
const CaptionsDashboard = Loader(lazy(() => import('src/content/dashboards/Captions')));
const ScriptsDashboard = Loader(lazy(() => import('src/content/dashboards/Scripts')));
const DriveDashboard = Loader(lazy(() => import('src/content/dashboards/Drive')));
const TriggersDashboard = Loader(lazy(() => import('src/content/dashboards/Triggers')));
const SuperSettings = Loader(lazy(() => import('src/content/dashboards/SuperSettings')));
const Payment = Loader(lazy(() => import('src/content/dashboards/Payment')));
const ProfitTracker = Loader(lazy(() => import('src/content/dashboards/ProfitTracker')));
const UserSettingsDashboard = Loader(lazy(() => import('src/content/dashboards/UserSettings')));

// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Transactions = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const UserManagement = Loader(
  lazy(() => import('src/content/applications/UserManagement'))
);

// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

// Auth
const Login = Loader(lazy(() => import('src/content/pages/Auth/Login')));
const AuthCallback = Loader(
  lazy(() => import('src/content/pages/Auth/Callback'))
);
const CompleteProfile = Loader(
  lazy(() => import('src/content/pages/Auth/CompleteProfile'))
);
const Logout = Loader(lazy(() => import('src/content/pages/Auth/Logout')));

const routes: RouteObject[] = [
  {
    path: 'auth',
    element: <BaseLayout />,
    children: [
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'callback',
        element: <AuthCallback />
      },
      {
        path: 'complete-profile',
        element: (
          <ProtectedRoute requireProfileComplete={false}>
            <CompleteProfile />
          </ProtectedRoute>
        )
      },
      {
        path: 'logout',
        element: <Logout />
      }
    ]
  },
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboards/main" replace />
      },
      {
        path: 'overview',
        element: <Navigate to="/dashboards/main" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="main" replace />
      },
      {
        path: 'main',
        element: <MainDashboard />
      },
      {
        path: 'agent',
        element: <AgentDashboard />
      },
      {
        path: 'agent-analytics',
        element: <AgentAnalyticsDashboard />
      },
      {
        path: 'tasks',
        element: <Tasks />
      },
      {
        path: 'emails',
        element: <EmailsDashboard />
      },
      {
        path: 'calendar',
        element: <CalendarDashboard />
      },
      {
        path: 'team',
        element: <TeamDashboard />
      },
      {
        path: 'elevenlabs',
        element: <ElevenLabsDashboard />
      },
      {
        path: 'render',
        element: <RenderDashboard />
      },
      {
        path: 'audome',
        element: <AudomeDashboard />
      },
      {
        path: 'captions',
        element: <CaptionsDashboard />
      },
      {
        path: 'scripts',
        element: <ScriptsDashboard />
      },
      {
        path: 'drive',
        element: <DriveDashboard />
      },
      {
        path: 'attendance',
        element: <AttendanceDashboard />
      },
      {
        path: 'messenger',
        element: <Messenger />
      },
      {
        path: 'settings',
        element: <UserSettingsDashboard />
      }
    ]
  },
  {
    path: 'admin',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="settings" replace />
      },
      {
        path: 'payment',
        element: <Payment />
      },
      {
        path: 'settings',
        element: <SuperSettings />
      },
      {
        path: 'triggers',
        element: <TriggersDashboard />
      },
      {
        path: 'profit-tracker',
        element: <ProfitTracker />
      }
    ]
  },
  {
    path: 'management',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="user-management" replace />
      },
      {
        path: 'user-management',
        element: <UserManagement />
      },
      {
        path: 'transactions',
        element: <Transactions />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },
  {
    path: '/components',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

export default routes;
